import { CostFrequency } from "@/Simulation/enums"
import { ITCostProfile, NodeCapitalCost, NodeMaintenanceCost } from "@/Simulation/interfaces"
import { NodeReferenceHelper } from "@/core/helpers"
import { Models } from "@ekkosense/predict-client-api"
import { DeviceModelType, Interface } from "@ekkosense/predict-performance-calculation"
import { WorkloadNodeParameter } from "./WorkloadNodeParameter"

/**
 *
 */
export class ItNodeParameter extends WorkloadNodeParameter<Models.ITNodeModel, Interface.ITNode> {
    protected async getProvisioningEvents(node: Models.ITNodeModel) {
        return (await this.getITProvisioning(node)).itProvisioningEvents
    }
    async fetch(node: Models.ITNodeModel) {
        return {
            ...await this.fetchBasic(node),
            ...await NodeReferenceHelper.getSupplierReference(node),
            itProvisioning: await this.getITProvisioning(node),
            itCostProfile: await this.getITCostProfile(node),
            deviceModelType: 'it'
        } as Interface.ITNode & {itCostProfile: ITCostProfile | null, deviceModelType: DeviceModelType | "it", name: string, id: string}
    }

    /**
     * Retrieves the item/It node cost profile
     * @param node
     */
    async getITCostProfile(node: Models.ITNodeModel): Promise<ITCostProfile | null> {

        let costProfile: ITCostProfile | null = null

        const nodeCostProfile = await node.getItCostProfile()

        if (nodeCostProfile) {

            costProfile = {} as ITCostProfile

            costProfile.nodeCapitalCosts = []
            costProfile.nodeMaintenanceCosts = []

            const nodeCapitalCosts = nodeCostProfile.getNodeCapitalCosts()
            for await (const nodeCapitalCost of nodeCapitalCosts) {

                const cost = {
                    type: "nodeCapitalCost",
                    name: nodeCapitalCost.getName(),
                    amount: nodeCapitalCost.getAmount(),
                    lifetimeYears: nodeCapitalCost.getLifetimeYears(),
                    startDate: new Date(nodeCapitalCost.getStartDate()!)

                } as NodeCapitalCost

                costProfile.nodeCapitalCosts.push(cost)
            }

            const nodeMaintenanceCosts = nodeCostProfile.getNodeMaintenanceCosts()
            for await (const nodeMaintenanceCost of nodeMaintenanceCosts) {

                const cost = {
                    type: "nodeMaintenanceCost",
                    name: nodeMaintenanceCost.getName(),
                    amount: nodeMaintenanceCost.getAmount(),
                    startDate: new Date(nodeMaintenanceCost.getStartDate()!),
                    endDate: new Date(nodeMaintenanceCost.getEndDate()!),
                    frequency: nodeMaintenanceCost.getFrequency() as CostFrequency


                } as NodeMaintenanceCost

                costProfile.nodeMaintenanceCosts.push(cost)
            }
        }

        return costProfile

    }

    /**
     * Retrieves item's (it node) IT Provisioning, in order
     *
     * @param itNodeModel
     * @returns
     */
    async getITProvisioning(itNodeModel: Models.ITNodeModel): Promise<Interface.ITProvisioning<Date>> {
        const provisioning = await itNodeModel.getItProvisioning()

        const provisioningEventsUnsorted: Interface.LoadProvisioningEvent<Date>[] = []
        if (provisioning) {
            const events = provisioning.getItProvisioningEvents()

            for await (const event of events) {
                const loadProfile = await event.getItLoadProfile()

                if (loadProfile) {
                    const loadHours: Interface.ITLoadHour[] = []

                    const eventLoadHours = loadProfile.getItLoadHours()

                    for await (const loadHour of eventLoadHours) {
                        loadHours.push({
                            hour: loadHour.getHour()!,
                            load: loadHour.getLoad()!,
                        })
                    }
                    const newEvent = {
                        power: event.getPower()!,
                        month: new Date(event.getMonth()!),
                        powerCapacity: event.getPowerCapacity()!,
                        itLoadProfile: {
                            defunct: loadProfile.getDefunct()!,
                            name: loadProfile.getName()!,
                            itLoadHours: loadHours
                        },
                    } as Interface.LoadProvisioningEvent<Date>

                    provisioningEventsUnsorted.push(newEvent)
                }
            }
        }

        return {
            itProvisioningEvents: provisioningEventsUnsorted.sort(
                (a, b) => a.month < b.month ? -1 :
                    (a.month === b.month ? 0 : 1)
            ),
        }
    }
}